import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, User, Menu, Flame } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";

const links = [
  { to: "/study", label: "Study" },
  { to: "/career", label: "Career" },
  { to: "/ideas", label: "Ideas" },
  { to: "/skills", label: "Skills" },
  { to: "/meghalaya", label: "Meghalaya" },
  { to: "/community", label: "Community" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-sl-bg border-b-2 border-black" data-testid="global-nav">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 py-4">
        <Link to="/" data-testid="brand-home" className="flex items-center gap-2 group">
          <span className="inline-block bg-sl-primary nb-border rounded-lg w-9 h-9 grid place-items-center font-heading font-black text-lg nb-shadow group-hover:nb-shadow-lg transition-all">E</span>
          <span className="font-heading text-2xl font-black tracking-tighter">Edu<span className="italic">Market</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => {
            const active = loc.pathname.startsWith(l.to);
            return (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
                className={`px-4 py-2 font-heading font-bold text-sm uppercase tracking-tight rounded-lg nb-border nb-hover ml-2 ${active ? "bg-sl-primary nb-shadow" : "bg-white"}`}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              {user.streak?.current > 0 && (
                <Link
                  to="/study"
                  data-testid="streak-flame"
                  className="hidden sm:inline-flex items-center gap-1 font-mono-sl text-[11px] uppercase bg-white nb-border rounded-full px-2 py-1 nb-hover"
                  title={`Longest streak: ${user.streak.longest || user.streak.current} days`}
                >
                  <motion.span
                    animate={{ scale: [1, 1.2, 1], rotate: [0, -6, 6, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    className="inline-block"
                  >
                    <Flame className="w-4 h-4 text-orange-500 fill-orange-400" />
                  </motion.span>
                  <span className="font-heading font-black">{user.streak.current}</span>
                  <span className="text-neutral-500">day{user.streak.current === 1 ? "" : "s"}</span>
                </Link>
              )}
              <Link to="/profile" data-testid="user-badge" className="hidden md:inline-flex items-center gap-2 font-mono-sl text-xs uppercase bg-sl-tertiary nb-border rounded-full px-3 py-1 nb-hover">
                <span className={`inline-grid place-items-center w-6 h-6 rounded-full nb-border text-sm ${user.avatar_bg ? `bg-sl-${user.avatar_bg}` : "bg-sl-primary"}`}>
                  {user.avatar_emoji || user.name?.[0]?.toUpperCase() || "U"}
                </span>
                {user.name?.split(" ")[0]}
              </Link>
              <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="p-2 rounded-lg bg-white nb-border nb-hover">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <Link to="/auth" data-testid="nav-signin" className="px-4 py-2 rounded-lg font-heading font-bold text-sm uppercase bg-black text-white nb-border nb-hover nb-shadow">
              Sign in
            </Link>
          )}
          <button className="md:hidden p-2 rounded-lg bg-white nb-border" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn">
            <Menu className="w-4 h-4" />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t-2 border-black bg-sl-bg px-5 py-3 flex flex-col gap-2" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="px-4 py-2 nb-border rounded-lg bg-white font-heading font-bold uppercase text-sm">{l.label}</Link>
          ))}
        </div>
      )}
    </header>
  );
}
